import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MessageCircle, Search, X, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { API } from '@/utils/api';
import { fmtH } from '../agendaUtils';
import { clientLabel, useClientSearch } from './ClientSection';

const Row = ({ k, v, testid }) => (
  <div className="flex gap-3 py-1.5 border-b border-slate-100 last:border-0 text-sm"><span className="w-32 shrink-0 text-slate-500">{k}</span><span className="font-medium" data-testid={testid}>{v}</span></div>
);

const durLabel = (m) => `${Math.floor(m / 60) ? `${Math.floor(m / 60)} h ` : ''}${m % 60 ? `${m % 60} min` : ''}`.trim();

const BillingPicker = ({ billing, setBilling }) => {
  const [q, setQ] = useState('');
  const results = useClientSearch(q);
  if (billing) return (
    <div className="flex items-center gap-2 text-sm p-2 rounded-lg bg-slate-50 border" data-testid="billing-selected">
      <span className="flex-1">Facturer à : <b>{clientLabel(billing)}</b></span>
      <button type="button" onClick={() => setBilling(null)} data-testid="billing-clear"><X className="w-4 h-4" /></button>
    </div>
  );
  return (
    <div className="relative">
      <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
      <Input value={q} onChange={e => setQ(e.target.value)} className="pl-8 h-9 text-sm" placeholder="Facturer à un autre client (facultatif)" data-testid="billing-search" />
      {results.slice(0, 5).map(c => (
        <button key={c.id} type="button" onClick={() => { setBilling(c); setQ(''); }} className="block w-full text-left text-sm px-3 py-1.5 hover:bg-slate-50" data-testid={`billing-result-${c.id}`}>{clientLabel(c)} <span className="text-xs text-slate-500">{c.city}</span></button>
      ))}
    </div>
  );
};

export const MotifPicker = ({ motifs, details, setDetails }) => {
  const toggle = (key, field) => setDetails(d => {
    const next = { ...d, [field]: d[field].includes(key) ? d[field].filter(x => x !== key) : [...d[field], key] };
    if (field === 'motifs') {
      const allowed = motifs.filter(m => next.motifs.includes(m.key)).flatMap(m => m.precisions);
      next.precisions = next.precisions.filter(p => allowed.includes(p));
    }
    return next;
  });
  const selected = motifs.filter(m => details.motifs.includes(m.key));
  return (
    <div className="space-y-3" data-testid="motif-picker">
      <div className="grid grid-cols-2 gap-2">
        {motifs.map(m => {
          const on = details.motifs.includes(m.key);
          return (
            <button key={m.key} type="button" onClick={() => toggle(m.key, 'motifs')} data-testid={`motif-${m.key}`}
              className={`flex items-center gap-2 text-left text-sm font-semibold px-3 py-2.5 rounded-xl border-2 transition-colors ${on ? 'border-slate-900 bg-[#FACC15]/20' : 'border-slate-200 hover:border-slate-400'}`}>
              <span className={`w-4 h-4 rounded flex items-center justify-center border ${on ? 'bg-slate-900 border-slate-900' : 'border-slate-300'}`}>{on && <Check className="w-3 h-3 text-white" />}</span>{m.label}
            </button>
          );
        })}
      </div>
      {selected.length > 0 && (
        <div className="space-y-1.5" data-testid="motif-precisions">
          <p className="text-xs text-slate-500">Précisions (facultatif)</p>
          <div className="flex flex-wrap gap-1.5">
            {selected.flatMap(m => m.precisions).map(p => (
              <button key={p} type="button" onClick={() => toggle(p, 'precisions')} data-testid={`precision-${p}`}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${details.precisions.includes(p) ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-300 hover:border-slate-900'}`}>{p}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Practical = ({ details, set, employees }) => (
  <div className="grid grid-cols-2 gap-3">
    <div><Label>Restitution souhaitée</Label><Input type="time" value={details.return_time} onChange={e => set('return_time')(e.target.value)} data-testid="booking-return-time-input" /></div>
    <div><Label>Collaborateur affecté</Label>
      <Select value={details.assigned_employee_id || 'none'} onValueChange={v => set('assigned_employee_id')(v === 'none' ? '' : v)}>
        <SelectTrigger data-testid="booking-employee-select"><SelectValue /></SelectTrigger>
        <SelectContent className="z-[80]"><SelectItem value="none">Non défini</SelectItem>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
      </Select></div>
    <label className="flex items-center justify-between gap-2 p-2.5 rounded-lg border text-sm font-medium">Client attend sur place<Switch checked={details.client_waits} onCheckedChange={set('client_waits')} data-testid="booking-client-waits-switch" /></label>
    <label className="flex items-center justify-between gap-2 p-2.5 rounded-lg border text-sm font-medium">Véhicule de remplacement<Switch checked={details.loaner_needed} onCheckedChange={set('loaner_needed')} data-testid="booking-loaner-switch" /></label>
  </div>
);

export const SummarySection = ({ motifs, details, setDetails, recap, billing, setBilling, sendWa, setSendWa, waNumber, isRdv }) => {
  const [employees, setEmployees] = useState([]);
  useEffect(() => { axios.get(`${API}/employees`).then(r => setEmployees((r.data || []).filter(e => e.is_active !== false && !e.is_admin))).catch(() => {}); }, []);
  const set = (k) => (v) => setDetails(d => ({ ...d, [k]: v?.target ? v.target.value : v }));
  const motifText = motifs.filter(m => details.motifs.includes(m.key)).map(m => m.label).join(', ');
  const emp = employees.find(e => e.id === details.assigned_employee_id);
  return (
    <section className="space-y-4" data-testid="booking-summary-section">
      <MotifPicker motifs={motifs} details={details} setDetails={setDetails} />
      <div>
        <Label className="text-base font-bold">{isRdv ? 'Objet du rendez-vous' : 'Demande du client / symptômes'}</Label>
        <Textarea rows={3} value={details.client_request} onChange={set('client_request')} className="mt-1 border-2 border-slate-900 text-base" placeholder={isRdv ? 'Ex : présentation du nouveau modèle, signature du contrat…' : 'Les mots du client, ex : « Service annuel et pose roues hiver »'} data-testid="booking-client-request-input" />
      </div>
      {isRdv
        ? <div><Label>Collaborateur</Label>
            <Select value={details.assigned_employee_id || 'none'} onValueChange={v => set('assigned_employee_id')(v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="booking-employee-select"><SelectValue /></SelectTrigger>
              <SelectContent className="z-[80]"><SelectItem value="none">Non défini</SelectItem>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
            </Select></div>
        : <><Practical details={details} set={set} employees={employees} /><BillingPicker billing={billing} setBilling={setBilling} /></>}
      <div className="rounded-xl border-2 border-slate-900 p-3" data-testid="booking-recap">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Récapitulatif</p>
        <Row k="Client" v={recap.client || '—'} testid="recap-client" />
        <Row k="Véhicule" v={recap.vehicle} testid="recap-vehicle" />
        <Row k="Date" v={recap.dateLabel} testid="recap-date" />
        <Row k="Horaires" v={recap.segments.map(s => `${fmtH(s.start)} – ${fmtH(s.end)}`).join(' puis ') || '—'} testid="recap-hours" />
        <Row k="Durée réservée" v={durLabel(recap.duration)} testid="recap-duration" />
        <Row k="Motif" v={motifText || '—'} testid="recap-reason" />
        {details.return_time && <Row k="Restitution" v={fmtH(details.return_time)} testid="recap-return" />}
        {(details.client_waits || details.loaner_needed) && <Row k="Options" v={[details.client_waits && 'Client attend sur place', details.loaner_needed && 'Véhicule de remplacement'].filter(Boolean).join(' · ')} testid="recap-options" />}
        <Row k="Poste / collaborateur" v={[recap.bay && `Poste ${recap.bay}`, emp && `${emp.first_name} ${emp.last_name}`].filter(Boolean).join(' · ') || '—'} testid="recap-bay" />
        <Row k="WhatsApp à" v={waNumber || 'Aucun numéro'} testid="recap-whatsapp" />
      </div>
      <label className={`flex items-center gap-3 p-3 rounded-xl border ${sendWa ? 'bg-emerald-50 border-emerald-300' : 'bg-white'}`}>
        <MessageCircle className="w-5 h-5 text-emerald-600" />
        <span className="flex-1 text-sm font-semibold">Envoyer la confirmation par WhatsApp</span>
        <Switch checked={sendWa} disabled={!waNumber} onCheckedChange={setSendWa} data-testid="booking-whatsapp-switch" />
      </label>
    </section>
  );
};
